import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { startRegistration } from "@simplewebauthn/browser";
import { useAuth } from "@/context/AuthContext";
import { Fingerprint, Trash2, Plus } from "lucide-react";

export default function BiometricsCard({ wrapperClass = "mb-5" }) {
  const { user } = useAuth();
  const [passkeys, setPasskeys] = useState([]);
  const [pkBusy, setPkBusy] = useState(false);

  const canPasskey = user && user.id && !user.impersonated;
  const loadPks = () => api.get("/auth/passkeys").then((r) => setPasskeys(r.data.passkeys || [])).catch(() => {});
  useEffect(() => { if (canPasskey) loadPks(); }, [canPasskey]);

  const addPasskey = async () => {
    setPkBusy(true);
    try {
      const opts = (await api.post("/auth/passkeys/register/options", { origin: window.location.origin })).data;
      const att = await startRegistration({ optionsJSON: opts });
      att._label = navigator.platform || "This device";
      await api.post("/auth/passkeys/register/verify", { origin: window.location.origin, credential: att });
      toast.success("Passkey added — you can now sign in with biometrics");
      loadPks();
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail) || (e2.name === "NotAllowedError" ? "Passkey setup was cancelled." : (e2.message || "Could not add passkey")));
    } finally { setPkBusy(false); }
  };

  const removePasskey = async (id) => {
    if (!window.confirm("Remove this passkey? You won't be able to sign in with it anymore.")) return;
    try { await api.delete(`/auth/passkeys/${id}`); toast.success("Passkey removed"); loadPks(); }
    catch { toast.error("Could not remove passkey"); }
  };

  const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleDateString() : "—"; } catch { return "—"; } };

  return (
    <div data-testid="biometrics-card" className={`bg-card border border-border rounded-lg shadow-sm p-5 ${wrapperClass}`}>
      <h2 className="font-serif text-xl tracking-tight mb-1 flex items-center gap-2"><Fingerprint className="h-5 w-5 text-primary" /> Biometrics &amp; Passkeys</h2>
      <p className="text-sm text-muted-foreground mb-4">Add a passkey to sign in with Face ID, Touch ID, fingerprint, or your device PIN — no password needed. Each device you use registers its own.</p>
      {!canPasskey ? (
        <p className="text-sm text-muted-foreground italic">{user?.impersonated ? "Passkeys aren't available while previewing as another user." : "Passkeys aren't available for this account."}</p>
      ) : (
        <>
          <div className="space-y-2 mb-4" data-testid="passkey-list">
            {passkeys.length === 0 && <p className="text-sm text-muted-foreground">No passkeys yet. Add one below to enable biometric sign-in.</p>}
            {passkeys.map((p) => (
              <div key={p.id} data-testid={`passkey-${p.id}`} className="flex items-center justify-between border border-border rounded-md px-3 py-2.5">
                <div className="flex items-center gap-2.5">
                  <Fingerprint className="h-4 w-4 text-primary" />
                  <div>
                    <div className="text-sm font-medium">{p.label || "Passkey"}</div>
                    <div className="text-xs text-muted-foreground">Added {fmt(p.created_at)}{p.last_used_at ? ` · last used ${fmt(p.last_used_at)}` : ""}</div>
                  </div>
                </div>
                <button data-testid={`remove-passkey-${p.id}`} onClick={() => removePasskey(p.id)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <button data-testid="add-passkey" onClick={addPasskey} disabled={pkBusy} className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-primary text-primary text-sm font-medium hover:bg-primary/5 disabled:opacity-60"><Plus className="h-4 w-4" /> {pkBusy ? "Waiting for device…" : "Add a passkey"}</button>
        </>
      )}
    </div>
  );
}
